import { shouldDisplayReviews } from '@/helpers';
import { trackMpEvent } from '@/helpers/analyticsTS';
import { SearchType } from '@/hooks/search/useSearch.helpers';
import { BaseSearchManagerOutput } from '@/hooks/search/useSearch.types';
import { DealsSearchState } from '@/reducers/dealsSearchReducer';
import { Tracking } from '@/types/analytics';
import { FILTER_ID, UrlSearchState } from '@/types/state/search';
import { CAMPAIGNS_SHOWN } from '../searchV2/constants/tracking';
import {
  getSearchFilters,
  getSearchLocationCity,
  getSearchLocationFilter,
  getSortedBy,
} from '../searchV2/helpers/tracking';
import { CampaignsShown, SearchContext, TrackingProps } from '../searchV2/types/tracking';
import { DealSearchItem, SistaminutenSearchItem } from './types/dealSearch';

function getCampaignsShown(type: SearchType, state: DealsSearchState): CampaignsShown | undefined {
  const campaigns = [];

  if (type === 'deals_preview') {
    const hasSistaminutenResults = (state.preview.sections || []).some(
      (section) => section.type === 'sistaminuten' && section.items.length > 0,
    );
    const hasDealsResults = (state.preview.sections || []).some(
      (section) => section.type === 'deals' && section.items.length > 0,
    );

    const hasBrandDealsResults = (state.preview.sections || []).some(
      (section) => section.type === 'brand_deals' && section.items.length > 0,
    );

    if (hasSistaminutenResults) {
      campaigns.push(CAMPAIGNS_SHOWN.SISTAMINUTEN);
    }

    if (hasDealsResults) {
      campaigns.push(CAMPAIGNS_SHOWN.DEALS);
    }

    if (hasBrandDealsResults) {
      campaigns.push(CAMPAIGNS_SHOWN.BRAND_DEALS);
    }
  } else {
    const hasDealsResults =
      type === 'deals' && state.list.results.some((result) => result.type === 'organic' && result.items.length > 0);
    const hasSistaminutenResults =
      type === 'deals_sistaminuten' &&
      state.list.results.some((result) => result.type === 'organic' && result.items.length > 0);
    const hasBrandDeals =
      type === 'brand_deals' &&
      state.list.results.some((result) => result.type === 'organic' && result.items.length > 0);

    const hasSponsored = state.list.results.some(
      (result) => result.type === 'sponsored' && result.section?.items?.length > 0,
    );

    if (hasDealsResults) {
      campaigns.push(CAMPAIGNS_SHOWN.DEALS);
    }

    if (hasSistaminutenResults) {
      campaigns.push(CAMPAIGNS_SHOWN.SISTAMINUTEN);
    }

    if (hasBrandDeals || hasSponsored) {
      campaigns.push(CAMPAIGNS_SHOWN.BRAND_DEALS);
    }
  }

  return campaigns.length > 0 ? campaigns : undefined;
}

export type SerpCardItem =
  | { type: 'deal'; data: DealSearchItem }
  | { type: 'sistaminuten'; data: SistaminutenSearchItem };

export function trackSerpCardClick({
  searchManager,
  item,
  state,
  screenName,
  resultSurface = 'serp',
  position,
}: {
  searchManager: BaseSearchManagerOutput;
  item: SerpCardItem;
  state: DealsSearchState;
  screenName: Tracking.ScreenName;
  resultSurface?: 'serp' | 'serp_map';
  position?: number;
}) {
  const properties: Tracking.CompanyClicked['properties'] = {
    screen_name: screenName,
    // nbr_search_results: searchManager.results > 0 ? searchManager.results : undefined,
    search_location_city: getSearchLocationCity(searchManager),
    search_location_filter: getSearchLocationFilter(searchManager),
    campaigns_shown: getCampaignsShown(searchManager.type, state),
    search_query: searchManager?.q.trim?.().length > 0 ? searchManager.q : undefined,
    result_surface: resultSurface,
    position,
  };

  // A deal card opens the deal page, but the click is still on that deal's place in the results.
  const place = item.type === 'deal' ? item.data.place : item.data;

  if (shouldDisplayReviews(item) && place?.rating?.count > 4) {
    properties['ratings_average'] = +place.rating.score.toFixed(1);
    properties['number_of_ratings'] = place.rating.count.toString();
  }
  properties['company_id'] = String(place.id);
  properties['marketplace_id'] = place.id;
  properties['salon_id'] = place.portalId;
  // The search's lane, not the card's kind: a Sista minuten card on the Deals landing or preview came from the
  // `deals_preview` search, so its click counts against that search.
  properties['search_variant'] = getSearchVariant(searchManager.type);

  trackMpEvent({ name: 'company_clicked', properties });
}

function getSearchVariant(type: SearchType): TrackingProps['search_variant'] {
  if (type === 'deals_preview') return 'deals_preview';
  if (type === 'deals_sistaminuten') return 'sistaminuten';
  if (type === 'brand_deals') return 'brand_deals';
  if (type === 'deals') return 'deals';
}

const SISTAMINUTEN_FILTER_IDS: number[] = [
  FILTER_ID.WELLNESSCARD,
  FILTER_ID.GIFTCARD,
  FILTER_ID.PAY_LATER,
  FILTER_ID.CERTIFIED,
];

function getAppliedPrefs(type: SearchType, prefs: UrlSearchState['prefs']): UrlSearchState['prefs'] {
  if (type !== 'deals_sistaminuten') return prefs;

  return prefs
    ?.split(',')
    .filter((pref) => SISTAMINUTEN_FILTER_IDS.includes(+pref))
    .join(',');
}

export function getSearchPerformedTrackingProps({
  type,
  urlState,
  state,
  context,
  screenName,
}: {
  type: SearchType;
  urlState: UrlSearchState;
  state: DealsSearchState;
  context: SearchContext;
  screenName: Tracking.ScreenName;
}): TrackingProps {
  const properties: TrackingProps = {
    screen_name: screenName,
    search_context: context,
    search_query: urlState?.q.trim?.().length > 0 ? urlState.q : undefined,
    search_location_filter: getSearchLocationFilter(urlState),
    search_location_city: getSearchLocationCity(urlState),
    campaigns_shown: getCampaignsShown(type, state),
    result_surface: 'serp',
  };

  if (type === 'deals_preview') {
    // ponytail: the preview has no total, so it counts the items its sections show
    properties['nbr_search_results'] = (state.preview?.sections ?? []).reduce(
      (count, section) => count + section.items.length,
      0,
    );
  } else {
    properties['sorted_by'] = getSortedBy(urlState);
    properties['search_filters'] = getSearchFilters(getAppliedPrefs(type, urlState.prefs));
    properties['nbr_search_results'] = state.list?.total;
  }

  properties['search_variant'] = getSearchVariant(type);

  return properties;
}
